import React, { useEffect, useState } from "react";
import { Button, NoticeBox } from "../ui";
import { getReportText } from "../reports/reportText";
import { canLoadEarlier, defaultRange, loadEarlier, maxRange, validateRange } from "../../utils/analysisWindow";
import "./AnalysisRangeBar.css";

// Period selector of the analysis screens (REPORT_ANALYSIS_WINDOW). The inputs are drafts: only a valid range is passed to `onChange`, an invalid one
// shows its reason and leaves the screen on the last good range. `window` is the range the server actually answered (shown so a limited range is never
// mistaken for all history).
export default function AnalysisRangeBar({ lang, range, onChange, window: answered, now }) {
  const text = getReportText(lang).analysisWindow;
  const [draft, setDraft] = useState(range);
  useEffect(() => setDraft(range), [range]);

  const check = validateRange(draft, now);
  const edit = (name, value) => {
    const next = { ...draft, [name]: value };
    setDraft(next);
    if (validateRange(next, now).ok) onChange(next);
  };
  const shown = answered || range;

  return (
    <section className="analysis-range-bar" aria-label={text.label}>
      <div className="analysis-range-bar__fields">
        <label>
          <span>{text.from}</span>
          <input type="month" value={draft.from} max={draft.to || undefined} onChange={(event) => edit("from", event.target.value)} aria-invalid={!check.ok} />
        </label>
        <label>
          <span>{text.to}</span>
          <input type="month" value={draft.to} min={draft.from || undefined} onChange={(event) => edit("to", event.target.value)} aria-invalid={!check.ok} />
        </label>
        <Button size="sm" appearance="outline" onClick={() => onChange(defaultRange(now))}>{text.last12}</Button>
        <Button size="sm" appearance="outline" onClick={() => onChange(maxRange(now))}>{text.last24}</Button>
        <Button size="sm" appearance="outline" disabled={!check.ok || !canLoadEarlier(range)} onClick={() => onChange(loadEarlier(range))}>
          {getReportText(lang).loadEarlier}
        </Button>
      </div>
      {check.ok ? (
        <p className="analysis-range-bar__note" role="status">
          {text.shown(shown.from, shown.to)}
          {!canLoadEarlier(range) && ` - ${text.capped}`}
        </p>
      ) : (
        <div role="alert"><NoticeBox variant="danger" size="sm">{text.errors[check.code]}</NoticeBox></div>
      )}
    </section>
  );
}

// Loading / error / empty line under the range bar. Renders nothing while the data is there.
export function AnalysisWindowStatus({ lang, loading, error, empty, onRetry }) {
  const text = getReportText(lang).analysisWindow;
  if (loading) return <p className="analysis-range-bar__note" role="status">{text.loading}</p>;
  if (error) {
    return (
      <div role="alert" className="analysis-range-bar__status">
        <NoticeBox variant="danger" size="sm">{text.error}</NoticeBox>
        <Button size="sm" appearance="outline" onClick={onRetry}>{getReportText(lang).retry}</Button>
      </div>
    );
  }
  if (empty) return <p className="analysis-range-bar__note" role="status">{text.empty}</p>;
  return null;
}
